import { useEffect, useMemo } from 'react';
import type { SortingState } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { mutate } from 'swr';
import useSWRInfinite from 'swr/infinite';

import type { CertificateTypeForm } from 'containers/CertificatesOverview/CertificateTypeEditingDialog/CertificateTypeEditingDialog';
import useUserCertificatesFilters from 'containers/CertificatesOverview/UserCertificatesTable/useUserCertificatesFilters';
import useHandleError from 'hooks/useHandleError';
import useShowAlert from 'hooks/useShowAlert';
import { checkStatus, del, post } from 'libs/apiClient';
import type {
  CertificatesMetadata,
  CertificateType,
  CompanyCertificateSettings,
  OverviewCertificate,
  UserCertificate,
} from 'model/CertificatesOverview/types';
import { SortingField } from 'model/CertificatesOverview/types';

import { useApi } from './base-api';

const certificateTypeBaseUrl = '/api/certificatetype';
const certificateV2ApiBaseUrl = '/api/v2/certificates';

export const certificatesMetadataUrl = `${certificateV2ApiBaseUrl}/metadata`;
export const overviewCertificatesUrl = `${certificateV2ApiBaseUrl}/types`;
export const defaultCertificatesPageSize = 30;

export const useGetOverviewCertificates = () =>
  useApi<OverviewCertificate[]>(overviewCertificatesUrl);

const getParams = (
  pageIndex: number,
  previousPageData: {
    more: boolean;
    data: UserCertificate[];
  } | null,
  sortingState: SortingState,
  filters: {
    typeId?: string;
    search?: string;
    onlyExpiredOrNotificationDateDue?: boolean;
    tagIds?: string;
    projectIds?: string;
    categoryId?: string;
  }
) => {
  const params = new URLSearchParams({
    page: '0',
    offset: defaultCertificatesPageSize.toString(),
  });
  if (sortingState[0]) {
    params.append(
      'sortBy',
      // Finding an enum string key that is a part of id (column key) and reading number value stored by that key
      // e.g. 'documents' is included in 'numberofdocuments'
      SortingField[
        Object.keys(SortingField).find((key) =>
          sortingState[0].id.toLowerCase().includes(key.toLowerCase())
        )!
      ]
    );
    params.append('descending', String(sortingState[0].desc));
  }
  if (filters.typeId) {
    params.append('typeId', filters.typeId);
  }
  if (filters.search) {
    params.append('search', filters.search);
  }

  if (filters.onlyExpiredOrNotificationDateDue) {
    params.append('onlyExpiredOrNotificationDateDue', 'true');
  }

  if (filters.tagIds) {
    params.append('tagIds', filters.tagIds);
  }

  if (filters.projectIds) {
    params.append('projectIds', filters.projectIds);
  }

  if (filters.categoryId) {
    params.append('categoryId', filters.categoryId);
  }

  if (previousPageData?.data && !previousPageData.more) {
    // no more posts to load
    return null;
  }
  // first page
  if (pageIndex === 0) return `/api/v2/certificates?${params}`;
  // load new page
  params.set('page', pageIndex.toString());
  return `/api/v2/certificates?${params}`;
};

/** Depends on URL search params */
export const useGetPagedUserCertificates = (
  sortingState: SortingState,
  categoryId?: string,
  typeId?: string,
  shouldSkip = false,
  revalidateOnMount = false
) => {
  const handleError = useHandleError();

  const { search, onlyExpired, tagIds, projectIds } =
    useUserCertificatesFilters();

  const {
    data,
    error,
    size,
    mutate: mutateCertificates,
    ...rest
  } = useSWRInfinite<{
    more: boolean;
    data: UserCertificate[];
  }>(
    (pageIndex, previousPageData) =>
      shouldSkip
        ? null
        : getParams(pageIndex, previousPageData, sortingState, {
            typeId,
            search,
            onlyExpiredOrNotificationDateDue: onlyExpired,
            tagIds,
            projectIds,
            categoryId,
          }),
    {
      revalidateOnFocus: false,
      revalidateAll: false,
      revalidateFirstPage: false,
      revalidateOnMount,
    }
  );

  useEffect(() => {
    if (error) {
      handleError(error);
    }
  }, [error]);

  // A bit of a hack to force re-fetch when the filters change if data was already loaded
  useEffect(() => {
    if (data) {
      mutateCertificates();
    }
  }, [search, onlyExpired, projectIds, categoryId]);

  const flatCertificates = useMemo(
    () =>
      data ? data.map((certificatesDate) => certificatesDate.data).flat() : [],
    [data]
  );
  const isFetchingCertificates = !data && !error;
  const isLoadingMore =
    isFetchingCertificates ||
    (size > 0 && data && typeof data[size - 1] === 'undefined');

  return {
    certificates: data,
    flatCertificates,
    size,
    error,
    isFetchingCertificates,
    isLoadingMore,
    mutate: mutateCertificates,
    ...rest,
  };
};

export const useGetCertificatesMetadata = (
  typeId?: string,
  shouldSkip = false
) => {
  const { search, onlyExpired, tagIds, projectIds } =
    useUserCertificatesFilters();

  let url: string | null = null;

  if (!shouldSkip) {
    const params = new URLSearchParams();
    if (typeId) params.append('typeId', typeId);
    if (search) params.append('search', search);
    if (onlyExpired) params.append('onlyExpiredOrNotificationDateDue', 'true');
    if (tagIds) params.append('tagIds', tagIds);
    if (projectIds) params.append('projectIds', projectIds);

    const query = params.toString();
    url = `${certificatesMetadataUrl}${query ? `?${query}` : ''}`;
  }

  return useApi<CertificatesMetadata>(url);
};

export const useGetCertificteTypeByTypeId = (typeId: string) => {
  const { data, ...rest } = useApi<CertificateType[]>(
    typeId ? `${certificateTypeBaseUrl}?typeId=${typeId}` : null
  );

  const certificateType = useMemo(() => data && data[0], [data]);

  return { certificateType, ...rest };
};

export const useCreateOrUpdateCertificateType = () => {
  const { t } = useTranslation();
  const handleError = useHandleError();
  const { showSuccessMessage } = useShowAlert();

  return async (
    newCertificateType: CertificateTypeForm & Partial<OverviewCertificate>
  ) => {
    mutate<OverviewCertificate[]>(
      overviewCertificatesUrl,
      async (data) => {
        try {
          const response = checkStatus(
            await post(certificateTypeBaseUrl, newCertificateType)
          );

          showSuccessMessage({
            message: newCertificateType.id
              ? t('CertificatesOverview.CertificateUpdated', {
                  typeId: newCertificateType.typeId,
                })
              : t('CertificatesOverview.CertificateCreated', {
                  typeId: newCertificateType.typeId,
                }),
          });

          const certificateType: CertificateType = await response.json();

          if (data) {
            return newCertificateType.id
              ? data.map((type) =>
                  type.id === certificateType.id
                    ? {
                        ...type,
                        typeId: certificateType.typeId,
                        description: certificateType.description,
                        mandatory: certificateType.mandatory,
                        allowMultiple: certificateType.allowMultiple,
                        expireWarning: certificateType.expireWarning,
                        categoryId: certificateType.categoryId,
                        categoryName: certificateType.categoryName,
                        usersToNotify: certificateType.usersToNotify,
                      }
                    : type
                )
              : [
                  ...data,
                  {
                    description: certificateType.description,
                    expiryDate: null,
                    expireWarning: certificateType.expireWarning,
                    id: certificateType.id,
                    mandatory: certificateType.mandatory,
                    numberOfUsers: 0,
                    typeId: certificateType.typeId,
                    categoryId: certificateType.categoryId,
                    categoryName: certificateType.categoryName,
                    usersToNotify: certificateType.usersToNotify,
                    usersToNotifyList: [],
                  },
                ];
          }

          return [
            {
              description: certificateType.description,
              expiryDate: null,
              expireWarning: certificateType.expireWarning,
              id: certificateType.id,
              mandatory: certificateType.mandatory,
              allowMultiple: certificateType.allowMultiple,
              numberOfUsers: 0,
              typeId: certificateType.typeId,
              categoryId: certificateType.categoryId,
              categoryName: certificateType.categoryName,
              usersToNotify: certificateType.usersToNotify,
              usersToNotifyList: [],
            },
          ];
        } catch (e) {
          handleError(e);
          return data;
        }
      },
      {
        optimisticData: (data) => {
          if (data) {
            return newCertificateType.id
              ? data.map((type) =>
                  type.id === newCertificateType.id
                    ? {
                        ...type,
                        ...newCertificateType,
                      }
                    : type
                )
              : [
                  ...data,
                  {
                    id: 'placeholder',
                    expiryDate: null,
                    numberOfUsers: 0,
                    categoryName: null,
                    usersToNotifyList: [],
                    ...newCertificateType,
                  },
                ];
          }

          return [
            {
              id: 'placeholder',
              expiryDate: null,
              numberOfUsers: 0,
              categoryName: null,
              usersToNotifyList: [],
              ...newCertificateType,
            },
          ];
        },
        revalidate: true,
      }
    );
  };
};

export const useDeleteCertificateType = () => {
  const { t } = useTranslation();
  const handleError = useHandleError();
  const { showSuccessMessage } = useShowAlert();

  return async (id: string, typeId: string) => {
    mutate<OverviewCertificate[]>(
      overviewCertificatesUrl,
      async (data) => {
        try {
          checkStatus(await del(`${certificateTypeBaseUrl}/${id}`));

          showSuccessMessage({
            message: t('CertificatesOverview.CertificateDeleted', { typeId }),
          });

          return data?.filter((type) => type.id !== id) || [];
        } catch (e) {
          handleError(e);
          return data;
        }
      },
      {
        optimisticData: (data) => data?.filter((type) => type.id !== id) || [],
        revalidate: false,
      }
    );
  };
};

/**
 * Mirrors `CertificateDownloadRequest.cs`. Every dimension the list narrows by is present, so an
 * "export everything I can see" download covers exactly the rows on screen. All of it is ignored
 * by the backend when `certificateIds` picks specific rows.
 */
interface CertificateDownloadFilters {
  certificateIds?: string[];
  typeId?: string;
  categoryId?: string;
  search?: string;
  onlyExpiredOrNotificationDateDue?: boolean;
  tagIds?: string;
  projectIds?: string[];
}

export const downloadCertificatesZip = async (
  filters: CertificateDownloadFilters
): Promise<Blob> => {
  const response = checkStatus(
    await post('/api/v2/certificates/download-attachments', filters)
  );

  return response.blob();
};

export const downloadCertificatesCSV = async (
  filters: CertificateDownloadFilters
): Promise<Blob> => {
  const response = checkStatus(
    await post('/api/v2/certificates/download-csv', filters)
  );

  return response.blob();
};

export const downloadCertificatesPDF = async (
  filters: CertificateDownloadFilters
): Promise<Blob> => {
  const response = checkStatus(
    await post('/api/v2/certificates/download-pdf', filters)
  );

  return response.blob();
};

export const useGetCompanyCertificateSettings = (fetch: boolean) =>
  useApi<CompanyCertificateSettings>(
    fetch ? `${certificateV2ApiBaseUrl}/company-settings` : null
  );

export const useUpdateCompanyCertificateSettings = () => {
  const { t } = useTranslation();
  const handleError = useHandleError();
  const { showSuccessMessage } = useShowAlert();

  return async (settings: CompanyCertificateSettings) => {
    mutate(
      `${certificateV2ApiBaseUrl}/company-settings`,
      async (data) => {
        try {
          const response = checkStatus(
            await post(`${certificateV2ApiBaseUrl}/company-settings`, settings)
          );

          const newData = await response.json();

          showSuccessMessage({
            message: t('CertificatesOverview.CompanyCertificateSettingsSaved'),
          });

          return newData || data;
        } catch (e) {
          handleError(e);
          return data;
        }
      },
      {
        optimisticData: settings,
        revalidate: false,
      }
    );
  };
};
